import { ReactNode } from "react";

import { Tooltip } from "antd";
import { clsx } from "clsx";
import { Link, To } from "react-router-dom";

import { RVRow } from "../../../../layouts/RVFlexbox/RVFlexbox";

import styles from "./RVGridActionableCell.module.scss";

export type RVGridActionableCellProps = {
    /** The content to display in the cell, generally text */
    content: ReactNode;
    /** Content to be appended after the text, generally an icon */
    suffix?: ReactNode;
    /** Content to be prepended before the text, generally an icon */
    prefix?: ReactNode;
    /** Tooltip text to display when hovering */
    tooltip?: string;
    /** Additional className for styling */
    className?: string;
    /** Optional click handler for the cell */
    onClick?: () => void;
    /** Optional link URL for the cell */
    linkTo?: To;
    /** Optional flag to open the link in a new tab */
    openInNewTab?: boolean;
};

/**
 * RVGridActionableCell - A reusable grid cell component that upon clicking has an action.
 * Commonly used for displaying links within a grid. It optionally takes prefix/suffix elements.
 */
export function RVGridActionableCell({
    content,
    suffix,
    prefix,
    tooltip,
    className,
    onClick,
    linkTo,
    openInNewTab,
}: RVGridActionableCellProps): JSX.Element {
    const cellContent = (
        <RVRow gap={1} className={clsx(styles.container, className)} alignItems="center" onClick={onClick}>
            {prefix && <span className={styles.extraContent}>{prefix}</span>}
            <Tooltip title={tooltip}>
                <span className={styles.content}>{content}</span>
            </Tooltip>
            {suffix && <span className={styles.extraContent}>{suffix}</span>}
        </RVRow>
    );

    const wrappedContent = linkTo ? (
        <Link
            to={linkTo}
            target={openInNewTab ? "_blank" : undefined}
            rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
            {cellContent}
        </Link>
    ) : (
        cellContent
    );
    return wrappedContent;
}
